import React, { useState, useEffect, useCallback } from 'react'
import { Button, Empty, Input, Checkbox, Pagination } from 'antd'
import classnames from 'classnames'
import sensors from '../../utils/sensors'
import { setLoading } from '../../actions/main'
import {
  setPathways,
  setCheckedMembers,
  setCurrentUser,
  fetchPathwaysByMembers,
  mapCheckUserDetail,
  viewPhoto,
  setUserListPanel
} from '../../actions/pathway'
import * as MapUtils from '../../utils/mapUtils'
import CONFIG from '../../constants/config'
import { capturePromiseException } from '../../utils/sentryUtils'
import { useAppSelector, useAppDispatch } from '../../app/hooks'
import type { Pathway, PathwayMember } from '../../slices/pathwaySlice'

const { Search } = Input

interface UserListProps {
  viewer: any
}

function UserList({ viewer }: UserListProps) {
  const dispatch = useAppDispatch()

  // Redux 状态
  const date = useAppSelector((state) => state.pathway.date)
  const groupId = useAppSelector((state) => state.group.groupId)
  const pathways = useAppSelector((state) => state.pathway.pathways)
  const takeMembers = useAppSelector((state) => state.pathway.takeMembers)
  const unTakeMembers = useAppSelector((state) => state.pathway.unTakeMembers)
  const checkedMembers = useAppSelector((state) => state.pathway.checkedMembers)
  const panel = useAppSelector((state) => state.pathway.panel)

  // 本地状态
  const [takePage, setTakePage] = useState(1)
  const [keyword, setKeyword] = useState('')
  const [searchValue, setSearchValue] = useState('')

  // 当日期变化时重置状态
  useEffect(() => {
    setTakePage(1)
    setKeyword('')
    setSearchValue('')
  }, [date])

  // 处理成员选中变化
  const handleCheckedMemberChange = useCallback(
    async (checked: boolean, userIds: (string | number)[]) => {
      if (!date) return
      let pathwaysInMap: Pathway[] = []
      let newUserIds: (string | number)[] = []
      if (checked) {
        // 去重后追加，保证顺序不变
        newUserIds = checkedMembers.concat(
          userIds.filter((userId) => !checkedMembers.includes(userId))
        )
        const notCachedUserIds = newUserIds.filter(
          (userId) => !pathways.find((pathway) => pathway.userID === userId)
        )
        dispatch(setCheckedMembers(newUserIds))
        if (notCachedUserIds.length) {
          // 新选中的用户，包含未请求路线的用户
          dispatch(setLoading(true))
          try {
            pathwaysInMap = await dispatch(
              fetchPathwaysByMembers({
                groupID: groupId,
                date: date.format(CONFIG.dateFormat),
                users: notCachedUserIds
              })
            ).then((pathwayData) => {
              const newPathways = pathways.concat(pathwayData.peoples)
              dispatch(setPathways(newPathways))
              dispatch(setLoading(false))
              return newPathways.filter((pathway) =>
                newUserIds.includes(pathway.userID)
              )
            })
          } catch (err) {
            capturePromiseException(err)
          }
        } else {
          pathwaysInMap = pathways.filter((pathway: Pathway) =>
            newUserIds.includes(pathway.userID)
          )
        }
      } else {
        newUserIds = checkedMembers.filter(
          (userId) => !userIds.includes(userId)
        )
        dispatch(setCheckedMembers(newUserIds))
        pathwaysInMap = pathways.filter((pathway: Pathway) =>
          newUserIds.includes(pathway.userID)
        )
      }
      MapUtils.drawPathsV2({
        pathways: pathwaysInMap,
        checkedMembers: newUserIds,
        createInfoWindow: (pathIndexInMap, markerIndex) =>
          MapUtils.createLocationInfoWindowV2({
            pathway: pathwaysInMap[pathIndexInMap],
            markerIndex,
            onButtonClick: () => {
              dispatch(
                mapCheckUserDetail(pathwaysInMap[pathIndexInMap], viewer)
              )
              sensors.track('web_pathway_homepage_click', {
                clickItem: 'routeNodeDetail',
                groupID: groupId,
                locationName:
                  pathwaysInMap[pathIndexInMap]?.locations?.[markerIndex]?.texts
                    ?.addr,
                photoNum:
                  pathwaysInMap[pathIndexInMap]?.locations?.[markerIndex]
                    ?.photos?.length || 0
              })
            },
            onViewPhoto: (photoIndex) => {
              dispatch(
                viewPhoto(
                  pathwaysInMap[pathIndexInMap],
                  markerIndex,
                  photoIndex,
                  viewer
                )
              )
              sensors.track('web_pathway_homepage_click', {
                clickItem: 'routeNodePic',
                groupID: groupId,
                locationName:
                  pathwaysInMap[pathIndexInMap]?.locations?.[markerIndex]?.texts
                    ?.addr,
                photoNum:
                  pathwaysInMap[pathIndexInMap]?.locations?.[markerIndex]
                    ?.photos?.length || 0
              })
            },
            canCheckDetail: true
          })
      })
      if (userIds.length === 1) {
        sensors.track('web_pathway_homepage_click', {
          clickItem: checked ? 'selectOne' : 'unSelectOne',
          groupID: groupId
        })
      }
    },
    [checkedMembers, pathways, groupId, date, viewer, dispatch]
  )

  // 处理全选变化
  const handleCheckAllChange = useCallback(
    (e: { target: { checked: boolean } }) => {
      const userIds = takeMembers
        .filter((member) => (member.nickname || '').includes(keyword.trim()))
        .slice(10 * (takePage - 1), 10 * takePage)
        .map((member) => member.userID)
      handleCheckedMemberChange(e.target.checked, userIds)
      if (e.target.checked) {
        sensors.track('web_pathway_homepage_click', {
          clickItem: 'selectAllMember',
          groupID: groupId
        })
      }
    },
    [keyword, takePage, takeMembers, groupId, handleCheckedMemberChange]
  )

  // 处理查看用户详情
  const handleCheckUserDetail = useCallback(
    async (member: PathwayMember) => {
      if (!date) return
      // 点击成员列表中的查看，查看单个用户
      const pathwaysInMap = pathways.filter(
        (pathway: Pathway) => pathway.userID === member.userID
      )
      let newPathways: Pathway[] = pathwaysInMap
      if (!pathwaysInMap.length) {
        dispatch(setLoading(true))
        try {
          newPathways = await dispatch(
            fetchPathwaysByMembers({
              groupID: groupId,
              date: date.format(CONFIG.dateFormat),
              users: [member.userID]
            })
          ).then((pathwayData) => {
            const newPathways = pathwayData.peoples
            dispatch(setPathways(pathways.concat(newPathways)))
            dispatch(setLoading(false))
            return newPathways
          })
        } catch (err) {
          capturePromiseException(err)
        }
      }
      MapUtils.drawPathsV2({
        pathways: newPathways,
        // 如果查看的人，是已选择列表中的某人，则使用已分配颜色；如果是未勾选的某人，则使用第一个颜色
        checkedMembers: checkedMembers.some(
          (userID) => userID === member.userID
        )
          ? checkedMembers
          : [member.userID],
        createInfoWindow: (pathIndexInMap, markerIndex) =>
          MapUtils.createLocationInfoWindowV2({
            pathway: newPathways[pathIndexInMap],
            markerIndex,
            onButtonClick: () => {
              dispatch(mapCheckUserDetail(newPathways[pathIndexInMap], viewer))
              sensors.track('web_pathway_homepage_click', {
                clickItem: 'routeNodeDetail',
                groupID: groupId,
                locationName:
                  newPathways[pathIndexInMap]?.locations?.[markerIndex]?.texts
                    ?.addr,
                photoNum:
                  newPathways[pathIndexInMap]?.locations?.[markerIndex]?.photos
                    ?.length || 0
              })
            },
            onViewPhoto: (photoIndex) => {
              dispatch(
                viewPhoto(
                  newPathways[pathIndexInMap],
                  markerIndex,
                  photoIndex,
                  viewer
                )
              )
              sensors.track('web_pathway_homepage_click', {
                clickItem: 'routeNodePic',
                groupID: groupId,
                locationName:
                  newPathways[pathIndexInMap]?.locations?.[markerIndex]?.texts
                    ?.addr,
                photoNum:
                  newPathways[pathIndexInMap]?.locations?.[markerIndex]?.photos
                    ?.length || 0
              })
            },
            canCheckDetail: false
          })
      })
      dispatch(setCurrentUser(member))
      sensors.track('web_pathway_homepage_click', {
        clickItem: 'memberDetail',
        groupID: groupId
      })
    },
    [pathways, checkedMembers, groupId, date, viewer, dispatch]
  )

  // 渲染成员过滤器
  const renderMemberFilter = () => {
    const members = takeMembers
      .filter((member) => (member.nickname || '').includes(keyword.trim()))
      .slice(10 * (takePage - 1), 10 * takePage)
    const checkedNumInPage = members.filter((member) =>
      checkedMembers.includes(member.userID)
    ).length
    const indeterminate =
      checkedNumInPage > 0 && checkedNumInPage < members.length
    const checkedAll =
      !!members.length &&
      members.every((member) => checkedMembers.includes(member.userID))
    const isShotPanel = panel === CONFIG.pathwayPanelTypes.SHOT_PANEL
    return (
      <div className="member-filter-wrapper">
        <div className="is-shot">
          <button
            className={classnames('shot', { active: isShotPanel })}
            onClick={() =>
              dispatch(setUserListPanel(CONFIG.pathwayPanelTypes.SHOT_PANEL))
            }
          >
            当日已拍
          </button>
          <button
            className={classnames('not-shot', { active: !isShotPanel })}
            onClick={() =>
              dispatch(
                setUserListPanel(CONFIG.pathwayPanelTypes.NOT_SHOT_PANEL)
              )
            }
          >
            当日未拍
          </button>
        </div>
        <div className="name-filter">
          <Search
            value={searchValue}
            style={{ display: 'flex', width: '312px', margin: '4px auto' }}
            placeholder="请输入姓名搜索"
            allowClear
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={(value) => {
              setKeyword(value)
              setTakePage(1)
              sensors.track('web_pathway_homepage_click', {
                clickItem: 'searchMember',
                groupID: groupId
              })
            }}
          />
        </div>
        {isShotPanel ? (
          <div className="thead">
            <div className="check-all">
              <Checkbox
                indeterminate={indeterminate}
                disabled={!members.length}
                checked={checkedAll}
                onChange={handleCheckAllChange}
              />
            </div>
            <div className="member-info">
              员工信息<span>（默认展示当天前五名）</span>
            </div>
            <div className="check-pathway">路线详情</div>
          </div>
        ) : (
          <div className="thead">
            <div
              className="member-info"
              style={{ width: '250px', paddingLeft: '10px' }}
            >
              员工信息
            </div>
            <div className="check-pathway">路线详情</div>
          </div>
        )}
      </div>
    )
  }

  // 渲染已拍照成员列表
  const renderTakeMemberList = () => {
    if (panel === CONFIG.pathwayPanelTypes.SHOT_PANEL) {
      const members = takeMembers
        .filter((member) => (member.nickname || '').includes(keyword.trim()))
        .slice(10 * (takePage - 1), 10 * takePage)
      return (
        <div className="member-list-wrapper">
          {members.length ? (
            members.map((member, index) => (
              <div className="member-item" key={index}>
                <Checkbox
                  checked={!!checkedMembers.includes(member.userID)}
                  onChange={(e) =>
                    handleCheckedMemberChange(e.target.checked, [member.userID])
                  }
                />
                <div className="member-info">
                  <img src={member.headimgurl} alt="" />
                  <div className="nickname-color">
                    <span>{member.nickname}</span>
                    <span
                      style={{
                        backgroundColor:
                          CONFIG.pathwayColors[
                            checkedMembers.findIndex(
                              (userID) => userID === member.userID
                            )
                          ]
                      }}
                    ></span>
                  </div>
                </div>
                <Button
                  type="link"
                  onClick={() => handleCheckUserDetail(member)}
                >
                  查看
                </Button>
              </div>
            ))
          ) : (
            <Empty
              style={{ marginTop: '180px' }}
              description={
                keyword.trim() ? '当前列表无该成员' : '拍照人员列表为空'
              }
            />
          )}
          {renderFooter()}
        </div>
      )
    }
    return null
  }

  // 渲染未拍照成员列表
  const renderUnTakeMemberList = () => {
    if (panel === CONFIG.pathwayPanelTypes.NOT_SHOT_PANEL) {
      const members = unTakeMembers.filter((member) =>
        (member.nickname || '').includes(keyword.trim())
      )
      return (
        <div className="member-list-wrapper">
          {members.length ? (
            members.map((member, index) => (
              <div className="member-item" key={index}>
                <div
                  className="member-info"
                  style={{ width: '250px', paddingLeft: '12px' }}
                >
                  <img src={member.headimgurl} alt="" />
                  <div className="nickname-color">
                    <span>{member.nickname}</span>
                  </div>
                </div>
                <Button
                  type="link"
                  onClick={() => {
                    dispatch(setCurrentUser(member))
                    sensors.track('web_pathway_homepage_click', {
                      clickItem: 'memberDetail',
                      groupID: groupId
                    })
                  }}
                >
                  查看
                </Button>
              </div>
            ))
          ) : (
            <Empty
              style={{ marginTop: '180px' }}
              description={
                keyword.trim() ? '当前列表无该成员' : '未拍照人员列表为空'
              }
            />
          )}
        </div>
      )
    }
    return null
  }

  // 渲染底部分页
  const renderFooter = () => {
    const itemRender = (
      current: number,
      type: string,
      originalElement: React.ReactNode
    ) => {
      if (type === 'prev') {
        return <a>上一页</a>
      }
      if (type === 'next') {
        return <a>下一页</a>
      }
      return originalElement
    }
    if (panel === CONFIG.pathwayPanelTypes.SHOT_PANEL) {
      const members = takeMembers.filter((member) =>
        (member.nickname || '').includes(keyword.trim())
      )
      if (members.length) {
        return (
          <div className="footer">
            <Pagination
              simple
              pageSize={10}
              current={takePage}
              total={members.length}
              itemRender={itemRender}
              onChange={(page) => setTakePage(page)}
            />
          </div>
        )
      } else {
        return null
      }
    } else {
      return null
    }
  }

  return (
    <div className="member-list">
      {renderMemberFilter()}
      {renderTakeMemberList()}
      {renderUnTakeMemberList()}
    </div>
  )
}

export default UserList
